/**
 * The Plans page's review-status ribbon: three groups a plan falls into by its
 * Resourcing status, read left to right as the plan moves through staffing.
 *
 * - Open — owned by sales to submit: Resourcing status Not set, or Rejected (stored as
 *   Returned — the RM sent it back, so it is in the author's hands again). Renamed from
 *   "Draft" on 2026-10-28.
 * - In Review — with Resourcing: In Review or Staffing, the two statuses the RM's
 *   Plans to Staff queue still holds.
 * - Staffed — off the queue.
 *
 * No new status values: this only groups the existing `plan_resourcing_status` ones.
 */

import type { PlanResourcingStatus, ResourcePlanSummaryView } from "@/schemas/resource-plans";

export const PLAN_REVIEW_STAGES = ["open", "inReview", "staffed"] as const;
export type PlanReviewStage = (typeof PLAN_REVIEW_STAGES)[number];

export const PLAN_REVIEW_STAGE_COPY: Record<PlanReviewStage, { label: string; hint: string }> = {
  open: { label: "Open", hint: "Owned by sales to submit" },
  inReview: { label: "In Review", hint: "With Resourcing" },
  staffed: { label: "Staffed", hint: "Off the queue" },
};

export function planReviewStage(status: PlanResourcingStatus | null | undefined): PlanReviewStage {
  if (!status) return "open";
  switch (status) {
    case "Returned":
      return "open";
    case "InReview":
    case "Staffing":
      return "inReview";
    case "Staffed":
      return "staffed";
  }
}

export interface PlanReviewCounts {
  open: number;
  inReview: number;
  staffed: number;
  /** How many of `open` were Rejected by the RM — shown as a note on the Open tile. */
  rejected: number;
  total: number;
}

type StagedPlan = Pick<ResourcePlanSummaryView, "resourcingStatus">;

export function countPlanReviewStages(plans: readonly StagedPlan[]): PlanReviewCounts {
  const counts: PlanReviewCounts = { open: 0, inReview: 0, staffed: 0, rejected: 0, total: 0 };
  for (const plan of plans) {
    counts[planReviewStage(plan.resourcingStatus)] += 1;
    if (plan.resourcingStatus === "Returned") counts.rejected += 1;
    counts.total += 1;
  }
  return counts;
}

export function matchesReviewStageFilter(
  plan: StagedPlan,
  filter: PlanReviewStage | "all",
): boolean {
  return filter === "all" || planReviewStage(plan.resourcingStatus) === filter;
}

export type PlanReviewFilter = PlanReviewStage | "all";

/**
 * Parses a remembered tile choice; anything unrecognised falls back to All Plans. A choice
 * remembered before the Draft tile was renamed Open still lands on Open.
 */
export function parsePlanReviewFilter(raw: string | null | undefined): PlanReviewFilter {
  if (raw === "draft") return "open";
  return PLAN_REVIEW_STAGES.find((stage) => stage === raw) ?? "all";
}
